'use client';

import { useState } from 'react';
import * as React from 'react';
import { User, Mail, MessageSquare, Send } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { countryDialCodes } from '@/lib/data/country-dial-codes';
import { CountrySelect } from './country-select';
import { PhoneInput } from './phone-input';
import { useToast } from '@/components/ui/toast';

interface ContactFormData {
    name: string;
    email: string;
    phone: string;
    country: string;
    message: string;
    website?: string; // Honeypot field (hidden from users)
}

interface ContactFormProps {
    onSubmit?: (data: ContactFormData) => void;
    className?: string;
    showTitle?: boolean;
    title?: string;
    source?: string; // Page source: 'contact', 'pricing', 'demo'
    demoName?: string; // Demo name when source is 'demo' (e.g., 'Binary MLM Software Demo')
    initialEmail?: string; // Pre-fill email field
    onClose?: () => void; // Callback when form should close (e.g., after successful submission)
}

const baseInputClasses = 'w-full pl-12 pr-4 py-2 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300';
const gradientOverlay = 'absolute inset-0 rounded-lg bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none';

export function ContactForm({
    onSubmit,
    className = '',
    showTitle = false,
    title,
    source = 'contact',
    demoName,
    initialEmail = '',
    onClose,
}: ContactFormProps) {
    const t = useTranslations('home.contact');
    const locale = useLocale();
    const [formData, setFormData] = useState<ContactFormData>({
        name: '', email: initialEmail, phone: '', country: '', message: '', website: '',
    });
    const [countryCode, setCountryCode] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [phoneFieldError, setPhoneFieldError] = useState<string>('');
    const { showToast, ToastComponent } = useToast();
    const phoneInputRef = React.useRef<HTMLInputElement>(null);
    // Store demoName in a ref to ensure it persists even if prop changes
    const demoNameRef = React.useRef<string | undefined>(demoName);
    
    // Update ref when demoName prop changes
    React.useEffect(() => {
        demoNameRef.current = demoName;
    }, [demoName]);

    // Fallback values for translations
    const placeholders = {
        name: t('form.namePlaceholder') || 'Name',
        email: t('form.emailPlaceholder') || 'Email',
        phone: t('form.phonePlaceholder') || 'Your phone number',
        message: t('form.messagePlaceholder') || 'Message',
        submit: t('form.submitLabel') || 'Submit',
        selectCountry: t('form.countryPlaceholder') || 'Select a country',
        phoneCountryError: t('form.phoneCountryError') || 'You must select a country before typing phone number',
        toastSelectCountry: t('form.toastSelectCountry') || 'Please select a country',
        toastPhoneRequired: t('form.toastPhoneRequired') || 'Please enter your phone number',
        toastInvalidPhone: t('form.toastInvalidPhone') || 'Please enter a valid phone number (7-15 digits)',
        toastMessageSent: t('form.toastMessageSent') || 'Message sent successfully!',
        toastMessageFailed: t('form.toastMessageFailed') || 'Failed to send message. Please try again.',
        toastConnectionError: t('form.toastConnectionError') || 'Failed to send message. Please check your connection and try again.',
    };

    // Always show country code with space when country is selected, even if phone is empty
    const phoneValue = countryCode ? `${countryCode} ${formData.phone}` : formData.phone;
    const codeLength = countryCode ? countryCode.length + 1 : 0; // +1 for the space

    // Update email when initialEmail changes
    React.useEffect(() => {
        if (initialEmail) {
            setFormData(prev => ({ ...prev, email: initialEmail }));
        }
    }, [initialEmail]);

    const validatePhone = (phone: string): boolean => {
        if (!phone || phone.trim() === '') return false; // Phone is required
        // Remove spaces, dashes, parentheses, and plus signs for validation
        const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
        // Check if it contains only digits and is between 7-15 digits
        return /^\d{7,15}$/.test(cleaned);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Validate country is selected
        if (!formData.country || formData.country === '') {
            showToast(placeholders.toastSelectCountry, 'error');
            setIsSubmitting(false);
            return;
        }

        // Validate phone number is required and valid format (7-15 digits)
        if (!formData.phone || formData.phone.trim() === '') {
            showToast(placeholders.toastPhoneRequired, 'error');
            setPhoneFieldError(placeholders.toastPhoneRequired);
            setIsSubmitting(false);
            return;
        }
        if (!validatePhone(formData.phone)) {
            showToast(placeholders.toastInvalidPhone, 'error');
            setPhoneFieldError(placeholders.toastInvalidPhone);
            setIsSubmitting(false);
            return;
        }
        setPhoneFieldError(''); // Clear error when valid

        if (onSubmit) {
            onSubmit(formData);
            setIsSubmitting(false);
            return;
        }

        try {
            // Build source with demo name if provided (use ref to ensure we have the latest value)
            const currentDemoName = demoNameRef.current || demoName;
            const finalSource = source === 'demo' && currentDemoName 
                ? `demo-${currentDemoName}` 
                : source;

            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    phone: countryCode ? `${countryCode} ${formData.phone}`.trim() : formData.phone,
                    locale,
                    source: finalSource,
                    sourceSite: process.env.NEXT_PUBLIC_CONTACT_WEBSITE || 'Business MLM',
                    countryCode,
                }),
            });

            const data = await res.json();

            if (!res.ok) {

                showToast(data.error || placeholders.toastMessageFailed, 'error');
                setIsSubmitting(false);
                return;
            }

            if (data.success) {
                // Reset form immediately and show success feedback
                setIsSubmitting(false);
                setFormData({ name: '', email: initialEmail || '', phone: '', country: '', message: '', website: '' });
                setCountryCode('');
                setPhoneFieldError('');
                // Show success toast with shorter duration
                showToast(placeholders.toastMessageSent, 'success', 1500);
                // Close modal faster
                if (onClose) {
                    setTimeout(() => {
                        onClose();
                    }, 500);
                }
            } else {
                showToast(data.error || placeholders.toastMessageFailed, 'error');
                setIsSubmitting(false);
            }
        } catch (error) {

            showToast(placeholders.toastConnectionError, 'error');
            setIsSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'country') {
            setFormData({ ...formData, country: value });
            const newCode = countryDialCodes[value] || '';
            setCountryCode(newCode);
            // Reset phone when country changes
            setFormData(prev => ({ ...prev, phone: '' }));
            // Clear phone field error when country is selected
            setPhoneFieldError('');
            return;
        }

        if (name === 'phone') {
            // Prevent typing if no country is selected
            if (!countryCode || !formData.country) {
                setPhoneFieldError(placeholders.phoneCountryError);
                // Reset the value and focus country field
                if (phoneInputRef.current) {
                    phoneInputRef.current.value = '';
                }
                setFormData({ ...formData, phone: '' });
                setTimeout(() => {
                    phoneInputRef.current?.blur();
                    const countrySelect = document.getElementById('country');
                    if (countrySelect) {
                        countrySelect.focus();
                    }
                }, 100);
                return;
            }

            // Clear error when country is selected and user is typing
            setPhoneFieldError('');

            // Remove country code and space from the value, keep only digits
            // Handle cases where space might be missing or extra spaces exist
            let cleanedValue = value;

            // If country code exists, remove it and any following spaces
            if (countryCode) {
                // Remove the country code (with or without space)
                cleanedValue = cleanedValue.replace(new RegExp(`^\\${countryCode}\\s*`), '');
            } else {
                // If no country code, just remove any leading + and digits
                cleanedValue = cleanedValue.replace(/^\+?\d+\s*/, '');
            }

            // Keep only digits
            const digitsOnly = cleanedValue.replace(/\D/g, '');
            setFormData({ ...formData, phone: digitsOnly });

            // Ensure cursor position is maintained after country code and space
            if (phoneInputRef.current && countryCode) {
                setTimeout(() => {
                    if (phoneInputRef.current) {
                        const newCursorPos = Math.max(codeLength, phoneInputRef.current.selectionStart || codeLength);
                        phoneInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                    }
                }, 0);
            }
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handlePhoneFocus = () => {
        if (!countryCode || !formData.country) {
            setPhoneFieldError(placeholders.phoneCountryError);
            // Blur the phone field and focus the country field instead
            setTimeout(() => {
                phoneInputRef.current?.blur();
                const countrySelect = document.getElementById('country');
                if (countrySelect) {
                    countrySelect.focus();
                }
            }, 100);
            return;
        }

        // Clear error when country is selected
        setPhoneFieldError('');

        if (phoneInputRef.current && countryCode) {
            // Position cursor after country code and space
            setTimeout(() => {
                if (phoneInputRef.current) {
                    const cursorPos = codeLength;
                    phoneInputRef.current.setSelectionRange(cursorPos, cursorPos);
                }
            }, 0);
        }
    };

    const handlePhoneClick = (e: React.MouseEvent<HTMLInputElement>) => {
        if (!countryCode || !formData.country) {
            setPhoneFieldError(placeholders.phoneCountryError);
            // Blur the phone field and focus the country field instead
            setTimeout(() => {
                phoneInputRef.current?.blur();
                const countrySelect = document.getElementById('country');
                if (countrySelect) {
                    countrySelect.focus();
                }
            }, 100);
            return;
        }

        // Clear error when country is selected
        setPhoneFieldError('');

        if (phoneInputRef.current && countryCode) {
            const input = phoneInputRef.current;
            const cursorPos = input.selectionStart || 0;

            // If cursor is before or within the country code, move it after
            if (cursorPos < codeLength) {
                setTimeout(() => {
                    input.setSelectionRange(codeLength, codeLength);
                }, 0);
            }
        }
    };

    const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!phoneInputRef.current) return;

        // If no country is selected, show error and prevent typing
        if (!countryCode || !formData.country) {
            // Allow navigation keys and escape
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', 'Escape'].includes(e.key)) {
                return;
            }

            // Show error message for any other key press
            if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter') {
                setPhoneFieldError(placeholders.phoneCountryError);
                e.preventDefault();
                // Focus the country field instead
                setTimeout(() => {
                    phoneInputRef.current?.blur();
                    const countrySelect = document.getElementById('country');
                    if (countrySelect) {
                        countrySelect.focus();
                    }
                }, 100);
            }
            return;
        }

        // Clear error when country is selected
        setPhoneFieldError('');

        const input = phoneInputRef.current;
        const cursorPos = input.selectionStart || 0;
        const selectionEnd = input.selectionEnd || 0;

        // Prevent editing the country code portion
        if (cursorPos < codeLength) {
            // Allow navigation keys
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
                if (e.key === 'ArrowLeft' && cursorPos === codeLength) {
                    e.preventDefault();
                    input.setSelectionRange(codeLength, codeLength);
                } else if (e.key === 'Home') {
                    e.preventDefault();
                    input.setSelectionRange(codeLength, codeLength);
                }
                return;
            }

            // Prevent deletion/editing of country code
            if (['Backspace', 'Delete'].includes(e.key)) {
                e.preventDefault();
                input.setSelectionRange(codeLength, codeLength);
                return;
            }

            // Prevent typing in country code area
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                input.setSelectionRange(codeLength, codeLength);
            }
        }

        // If selection includes country code, prevent deletion
        if (selectionEnd < codeLength && ['Backspace', 'Delete'].includes(e.key)) {
            e.preventDefault();
            input.setSelectionRange(codeLength, codeLength);
        }

        // Prevent deletion of the space after country code
        // The space is at position countryCode.length, cursor should be at codeLength (after space)
        const spacePosition = countryCode.length;
        if (cursorPos === spacePosition && e.key === 'Backspace') {
            e.preventDefault();
            input.setSelectionRange(codeLength, codeLength);
        }

        // If cursor is at the space position, prevent any editing
        if (cursorPos === spacePosition && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End' && e.key !== 'Tab') {
            e.preventDefault();
            // Move cursor to after the space
            input.setSelectionRange(codeLength, codeLength);
        }
    };

    return (
        <div className={className}>
            {ToastComponent}
            {showTitle && (
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-5">
                    {title || 'Send us a Message'}
                </h2>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div className="group relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                        <User className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
                    </div>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300"
                        placeholder={placeholders.name}
                        required
                    />
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
                </div>

                {/* Email Field */}
                <div className="group relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                        <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
                    </div>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300"
                        placeholder={placeholders.email}
                        required
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
                </div>

                {/* Country Field */}
                <CountrySelect
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  placeholder={placeholders.selectCountry}
                />

                {/* Phone Field */}
                <PhoneInput
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  countryCode={countryCode}
                  country={formData.country}
                  onChange={handleChange}
                  error={phoneFieldError}
                  required
                  placeholder={t('form.phonePlaceholder')}
                />
                {/* Message Field */}
                <div className="group relative">
                    <div className="absolute left-4 top-6 z-10">
                        <MessageSquare className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
                    </div>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={3}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300 resize-none"
                        placeholder={t('form.messagePlaceholder')}
                        required
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
                </div>

                {/* Honeypot field - hidden from users, bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                    />
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full px-8 py-4 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
                >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        {isSubmitting ? 'Sending...' : t('form.submitLabel')}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </button>
            </form>
        </div>
    );
}

